import React, { useState, useMemo } from 'react';
import { Beef } from 'lucide-react';
import CalculatorLayout from '../../components/calculators/CalculatorLayout';
import { WeightInput } from '../../components/calculators/MeasureInputs';
import { SelectField } from '../../components/calculators/FormFields';
import { EmptyResult, PrimaryResult, StatRow } from '../../components/calculators/ResultCard';
import { calculateProteinRange, PROTEIN_RANGES, ACTIVITY_FACTORS } from '../../lib/calculatorUtils';

const goalOptions = Object.entries(PROTEIN_RANGES).map(([k, v]) => ({
  value: k, label: `${v.label}: ${v.min}–${v.max} g/kg`
}));

const activityOptions = Object.entries(ACTIVITY_FACTORS).map(([k, v]) => ({
  value: k, label: v.label
}));

const ProteinIntakeCalculatorPage = () => {
  const [unit, setUnit] = useState('metric');
  const [weightKg, setWeightKg] = useState('');
  const [goal, setGoal] = useState('muscle_gain');
  const [activity, setActivity] = useState('moderate');

  const result = useMemo(() => {
    const w = parseFloat(weightKg);
    if (!w || w < 20) return null;
    const r = calculateProteinRange(w, goal);
    // Slight bump for very active / athlete in non-athlete goal
    let bumped = r.recommended;
    if (goal !== 'athlete' && (activity === 'active' || activity === 'athlete')) {
      bumped = Math.round(r.recommended * 1.05);
    }
    return { ...r, recommended: bumped, perMeal3: Math.round(bumped / 3), perMeal4: Math.round(bumped / 4) };
  }, [weightKg, goal, activity]);

  const goalLabel = PROTEIN_RANGES[goal]?.label;

  const inputs = (
    <div className="space-y-5" data-testid="protein-inputs">
      <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">Protein Profile</h2>
      <p className="text-slate-400 text-sm mb-5">Based on evidence-informed protein ranges per kg body weight.</p>
      <WeightInput unit={unit} onUnitChange={setUnit} weightKg={weightKg} onWeightKgChange={setWeightKg} />
      <SelectField id="protein-goal" label="Goal" value={goal} onChange={setGoal} options={goalOptions} testId="protein-goal-select" required />
      <SelectField id="protein-activity" label="Activity Level" value={activity} onChange={setActivity} options={activityOptions} testId="protein-activity-select" required />
    </div>
  );

  const resultNode = result ? (
    <div className="flex flex-col h-full">
      <PrimaryResult label="Recommended Daily Protein" value={result.recommended} unit="g/day" accent="from-rose-500 to-pink-500" testId="protein-result-recommended" />
      <div className="mt-8 space-y-1">
        <StatRow label="Daily protein range" value={`${result.min} – ${result.max}`} unit="g" color="text-rose-300" testId="protein-range" />
        <StatRow label="Goal" value={goalLabel} testId="protein-goal-display" />
        <StatRow label="Per meal (3 meals/day)" value={result.perMeal3} unit="g" testId="protein-per-meal-3" />
        <StatRow label="Per meal (4 meals/day)" value={result.perMeal4} unit="g" testId="protein-per-meal-4" />
        <StatRow label="Calories from protein" value={(result.recommended * 4).toLocaleString()} unit="kcal" testId="protein-calories" />
      </div>

      <div className="mt-6 bg-slate-800/40 border border-slate-700/40 rounded-xl p-4">
        <p className="text-slate-300 text-sm font-semibold mb-2">Quality protein sources</p>
        <p className="text-slate-400 text-xs leading-relaxed">Eggs, paneer, dal/legumes, fish, chicken, Greek yoghurt, tofu, soya chunks, whey protein.</p>
      </div>

      <p className="text-slate-500 text-xs mt-6 leading-relaxed">
        Distribute protein across 3–4 meals for optimal muscle protein synthesis. Pregnant, lactating, or medically restricted individuals should consult a clinician.
      </p>
    </div>
  ) : (
    <EmptyResult message="Enter your weight to see your recommended daily protein intake." />
  );

  return (
    <CalculatorLayout
      slug="protein-intake"
      title="Protein Intake Calculator"
      subtitle="Find your evidence-informed daily protein target based on your weight, goal, and activity level."
      metaDescription="Calculate your daily protein intake with the TCUBE FITNESS Protein Calculator, supporting fat loss, muscle gain, maintenance and athlete goals."
      icon={Beef}
      accentGradient="from-rose-500 to-pink-500"
      accentColor="text-rose-400"
      badgeBg="bg-rose-500/10"
      badgeBorder="border-rose-500/30"
      inputs={inputs}
      result={resultNode}
      formulaText={
        <>
          <p><strong className="text-slate-300">Recommended ranges (g per kg body weight):</strong></p>
          <p>Fat Loss: 1.6–2.0 · Muscle Gain: 1.8–2.2 · Maintenance: 1.2–1.6 · Athlete: 2.0–2.4</p>
          <p>Active individuals may benefit from the upper end of their range.</p>
        </>
      }
    />
  );
};

export default ProteinIntakeCalculatorPage;
